import { colors } from '@globals/themes/defaultTheme'
import { MediaObjectUrl, MediaVariantEnum } from '@kudos/http-client'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import { SxProps } from '@mui/material/styles'
import { forwardRef, useEffect, useState } from 'react'
import useAvatarConfig from './useAvatarConfig'

const contentStyle: SxProps = {
  color: colors.grayDark,
  alignItems: 'center',
  justifyContent: 'center',
}

const avatarStyle: SxProps = {
  display: 'inline-flex',
  borderRadius: '50%',
  borderStyle: 'solid',
  borderWidth: 2,
  borderColor: colors.white,
}

export type AvatarSize = number

type AvatarFontUnit = 'px' | 'vh'

type AvatarWrapperProps = {
  size?: AvatarSize
  media?: MediaObjectUrl | null
  src?: string
  alt?: string
  fontUnit?: AvatarFontUnit
  children?: React.ReactNode
  hideDropShadow?: boolean
  /* optional override for images that do not have avatar variants */
  mediaVariant?: MediaVariantEnum
  avatarBoxStyle?: SxProps
  tooltipTitle?: string
  backgroundColor?: string
  translate?: 'yes' | 'no'
  // Opt out of native lazy loading for above-the-fold avatars (e.g. the nav
  // avatar or the profile header) that should be fetched immediately.
  eager?: boolean
  /** Optional overlay (e.g. milestone badge) rendered inside the avatar's box. */
  adornmentOverlay?: React.ReactNode
}

const AvatarWrapper = forwardRef<HTMLDivElement, AvatarWrapperProps>(
  (
    {
      size = 40,
      media,
      src,
      alt,
      fontUnit = 'px',
      children,
      hideDropShadow = false,
      mediaVariant,
      avatarBoxStyle = {},
      tooltipTitle,
      backgroundColor = colors.blueUltraLight,
      translate = 'yes',
      eager = false,
      adornmentOverlay,
      ...rest
    },
    ref,
  ) => {
    const { fontSize, avatarUrl } = useAvatarConfig(size, media || null, mediaVariant)
    // Initialise synchronously so an already-known `src` (e.g. an explicit photo
    // or a deterministic default avatar) is applied on the first paint. Deferring
    // this to the effect left the MUI Avatar with no `src` and no children on the
    // initial render, briefly showing an empty circle for members without an
    // uploaded avatar. The effect keeps it in sync as the async-resolved
    // `avatarUrl` settles, and the `onError` handler can still override it.
    const [imageSrc, setImageSrc] = useState<string | undefined>(src ?? avatarUrl)
    useEffect(() => {
      setImageSrc(src ?? avatarUrl)
    }, [avatarUrl, src])
    const sizing = `${size}${fontUnit}`

    return (
      <Box
        ref={ref}
        {...rest}
        sx={[
          ...(Array.isArray(avatarBoxStyle) ? avatarBoxStyle : [avatarBoxStyle]),
          adornmentOverlay ? { position: 'relative', display: 'inline-flex' } : {},
        ]}
        tabIndex={0}>
        <Tooltip title={tooltipTitle} placement="top" translate={translate}>
          <Avatar
            src={imageSrc}
            slotProps={{
              img: {
                loading: eager ? 'eager' : 'lazy',
                decoding: 'async',
                // If there is an error while using media variants
                // we fallback to the original image.
                onError: () => avatarUrl && setImageSrc(media?.url),
              },
            }}
            alt={alt}
            sx={{
              ...avatarStyle,
              boxShadow: hideDropShadow ? 'unset' : colors.boxShadow,
              height: sizing,
              width: sizing,
              borderWidth: size >= 88 ? 4 : 2,
              backgroundColor,
            }}>
            <Stack
              sx={{
                ...contentStyle,
                fontSize: `${fontSize}${fontUnit}`,
                // below is intended for fallback icon only
                '& .MuiSvgIcon-root': {
                  fontSize: `${fontSize * 2}${fontUnit}`,
                  color: colors.blueLight,
                },
              }}>
              {children}
            </Stack>
          </Avatar>
        </Tooltip>
        {adornmentOverlay}
      </Box>
    )
  },
)

AvatarWrapper.displayName = 'AvatarWrapper'

export default AvatarWrapper
